import './TimelineView.scss';

import dayjs from 'dayjs';
import { FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { getQueryString } from '@sonar-software/sonar-http';
import { GroupedChangesChartParams } from './GroupedChangesChart';
import { keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import { PaginatedTableQueryParams } from '@ui/tables';
import { TimelineDetailsTable } from './DetailsTable';
import { TimelineFiltersParams } from './filters/TimelineFilters';
import { TimelineSummary } from './Summary';

import { coreActions, TimelineDetailsTableColumnKey, TimelineGroupingType } from '@sonar-software/sonar-core';
import { getPastDate, parseQueryString } from '@sonar-software/sonar-ts-lib';
import { parseObjectFilter, stringifyObjectFilter } from './@arch/lib';
import React, { useCallback } from 'react';
import { SonarCard, ViewArea } from '@ui/general';
import { useAction, useCurrentCustomer, useOnMount, useOnUnmount, useQueryParams } from '@hooks';
import { useLocation, useNavigate } from 'react-router-dom';

function isDetailsParamChange(params: Partial<TimelineQueryParams>): boolean {
	const { count, key, sortBy, sortDirection, paginationDirection, ...rest } = params;

	const hasOtherKeys = Object.keys(rest).length > 0;

	if (hasOtherKeys) {
		return false;
	}

	return Boolean(count) ||
		Boolean(paginationDirection) ||
		Boolean(key) ||
		(!key && Object.keys(params).includes('key')) ||
		Boolean(sortBy) ||
		Boolean(sortDirection);
}

export type TimelineQueryParams = PaginatedTableQueryParams<TimelineDetailsTableColumnKey> &
	TimelineFiltersParams &
	GroupedChangesChartParams &
	{ tzoffset: number };

export const defaultTimelineFilterParams: Omit<TimelineFiltersParams, 'tzoffset'> = {
	applicationIds: [],
	isFollowing: undefined,
	modifiedByUsernames: [],
	objectFilter: {},
	objectNameFilter: '',
	system: true,
	tagIds: []
};

export const defaultTimelineParams: TimelineQueryParams = {
	...defaultTimelineFilterParams,
	end: '',
	start: '',
	timelineGroupingType: TimelineGroupingType.Day,
	tzoffset: 0
};

export const TimelineView: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { timelineRetentionPeriod } = useCurrentCustomer();

	const getTimelineDetails = useAction(coreActions.requestGetCoreTimelineDetails);
	const getTimelineGroupedSummary = useAction(coreActions.requestGetCoreTimelineGroupedSummary);
	const getTimelineSummary = useAction(coreActions.requestGetCoreTimelineSummary);
	const getTimelineSynopsis = useAction(coreActions.requestGetCoreTimelineSynopsis);
	const getTimelineUserChanges = useAction(coreActions.requestGetCoreTimelineUserChanges);

	const resetTimelineDetails = useAction(coreActions.resetGetCoreTimelineDetails);
	const resetTimelineGroupedSummary = useAction(coreActions.resetGetCoreTimelineGroupedSummary);
	const resetTimelineSummary = useAction(coreActions.resetGetCoreTimelineSummary);
	const resetTimelineSynopsis = useAction(coreActions.resetGetCoreTimelineSynopsis);
	const resetTimelineUserChanges = useAction(coreActions.resetGetCoreTimelineUserChanges);

	const updateUrl = useCallback(
		(queryObject: TimelineQueryParams & { objectFilter: string}) => {
			const url = `${FULL_ROUTES.TIMELINE}?${getQueryString(queryObject)}`;

			navigate(url, { replace: true });
		},
		[ navigate ]
	);

	const { queryParams, updateParams } = useQueryParams<TimelineQueryParams>({
		defaultParams: {
			...defaultTimelineParams,
			start: getPastDate(Math.min(timelineRetentionPeriod ?? 30, 30))
		},
		fallbackParams: keyPaginationFallbackQueryParams,
		onUpdate: (newParams, prev, combined) => {
			const singleChange = Object.keys(newParams).length === 1;
			const onlyGroupingChange = singleChange && newParams.timelineGroupingType;
			const onlyModifiedByChange = singleChange && newParams.modifiedByUsernames;

			const {
				count,
				key,
				objectFilter,
				paginationDirection,
				sortBy,
				sortDirection,
				timelineGroupingType,
				...summaryRequest
			} = combined;

			const filterParams = {
				...summaryRequest,
				objectFilter: stringifyObjectFilter(objectFilter ?? {})
			};

			const detailParams = {
				...filterParams,
				count,
				key,
				paginationDirection,
				sortBy,
				sortDirection
			};

			updateUrl({
				...combined,
				objectFilter: filterParams.objectFilter
			});

			if (isDetailsParamChange(newParams)) {
				getTimelineDetails(detailParams);

				return;
			}

			getTimelineGroupedSummary({
				...filterParams,
				timelineGroupingType
			});

			if (!onlyGroupingChange && !onlyModifiedByChange) {
				const { modifiedByUsernames, system, ...userChangesParams } = filterParams;
				getTimelineUserChanges(userChangesParams);
			}

			if (!onlyGroupingChange) {
				getTimelineSummary(summaryRequest);
				getTimelineSynopsis(filterParams);
				getTimelineDetails(detailParams);
			}
		}
	});

	useOnMount(() => {
		const urlParams = parseQueryString(location.search) as TimelineQueryParams & { objectFilter: string };

		updateParams({
			...urlParams,
			objectFilter: parseObjectFilter(urlParams.objectFilter ?? ''),
			tzoffset: dayjs().utcOffset()
		});
	});

	useOnUnmount(() => {
		resetTimelineDetails();
		resetTimelineGroupedSummary();
		resetTimelineSummary();
		resetTimelineSynopsis();
		resetTimelineUserChanges();
	});

	return (
		<ViewArea
			direction='vertical'
			fullWidth
			id='timeline-view'
			showPeriscopes>
			<SonarCard noSpacing>
				<TimelineSummary
					onChange={ updateParams }
					params={ queryParams } />
				<TimelineDetailsTable
					onChange={ updateParams }
					params={ queryParams } />
			</SonarCard>
		</ViewArea>
	);
};
